import React from 'react';

import autoBind from 'react-autobind';
import { If, Then } from 'react-if'
import Switch, { Case } from 'react-switch-case';
import { withRouter } from "react-router-dom";

// Material UI fonts
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

// import other components within the same project here
import Config from "../Config.jsx";
import Localization from "../Localization.jsx";
import API from "../tools/API.jsx";
import AuthService from "../tools/AuthService.js";
import UserDataProfile from '../dataProviders/UserDataProfile.jsx';
import LoginPage from "../pages/LoginPage.jsx";
import LocaleViewerPage from "../pages/LocaleViewerPage.jsx";
import LoginCreateSession from "../tools/LoginCreateSession.jsx";
import TopBar from "../views/TopBar.jsx";
import HotspotViewerPage from "../pages/HotspotViewerPage.jsx";
import Loading from "../views/Loading.jsx";
import ConfirmDialog from "../views/ConfirmDialog.jsx";
import Notification from "../views/Notification.jsx"
import EdCityUnlinkedDialog from "../views/EdCityUnlinkedDialog.jsx";
import RetrieverPage from "../pages/RetrieverPage.jsx"
import ThreeDJobManager from "../tools/ThreeDJobManager.js";

// THEME IS NOW PROVIDED IN INDEX.JS
// import { ThemeProvider, createTheme } from '@mui/material/styles'; 
import { indigo, pink } from '@mui/material/colors';

const STATUS_REQUIRE_LOGIN = -1;
const STATUS_VIEW_LOCALE = 0;
const STATUS_VIEW_HOTSPOT = 1;
const STATUS_VIEW_RETRIEVER = 2;


const styles = {
  loadingOverlay: {
    position: 'fixed',
    width: '100%',
    height: '100%',
    zIndex: '99998',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    filter: 'blur(10px)',
  },
  none: {
  },
}

// Set the theme colours here
// THEME IS NOW PROVIDED IN INDEX.JS
/*
const theme = createTheme({
  palette: {
    primary: {
      main: '#ad1179', // Ensure this is the desired primary color
    },
    secondary: {
      main: '#b8a3bf',
    },
    halfToneColour: {
      main: '#999999',
    },  
    mutedColour: {
      main: '#cccccc',
    },  
  },
  typography: {

    body1: {
      fontWeight: 300,
      fontSize: '1rem',
      color: 'rgba(0, 0, 0, 0.7)',
    },
    body2: {
      fontWeight: 500,
      fontSize: '1rem',
      color: 'rgba(0, 0, 0, 0.7)',
    },
    body3: {
      fontWeight: 'bold',
      fontSize: '0.5rem',
      color: 'rgba(0, 0, 0, 0.7)',
    },
    fontFamily: [
      "CeraPro",
      "Noto Sans TC",
      "Roboto",
      '"Helvetica Neue"',
    ].join(','),
  },
  components: {
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          height: '48px',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          height: '32px',
          minHeight: '32px',
          display: 'flex',
          alignItems: 'center',
          padding: '0 24px 0 8px',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          height: '32px',
          padding: '0 14px',
        },
      },
    },
  },
});
*/

const keys = [];
let previousKey;

class RootPage extends React.Component {

  STATUS_REQUIRE_LOGIN() {
    return STATUS_REQUIRE_LOGIN;
  }

  STATUS_VIEW_LOCALE() {
    return STATUS_VIEW_LOCALE;
  }

  STATUS_VIEW_HOTSPOT() {
    return STATUS_VIEW_HOTSPOT;
  }

  STATUS_VIEW_RETRIEVER() {
    return STATUS_VIEW_RETRIEVER;
  }

  constructor(props) {
    super(props);
    this.refs = React.createRef();

    //window.onbeforeunload = function(){return false;} //refresh the page, prompt the user to confirm.

    // Check if this is an OAuth callback to avoid showing login page flash
    const params = new URLSearchParams(window.location.search);
    const isOAuthCallback = params.get('edconnect') === '1';
    
    // state variables here
    this.state = {
      status: isOAuthCallback ? STATUS_VIEW_LOCALE : STATUS_REQUIRE_LOGIN,
      currentLocale: null,
      isLoading: isOAuthCallback, // Show loading during OAuth processing
      hasShownStorageLimitDialog: false,
      isFreshLogin: false,
      // Why a third-party sign-in was refused, or null. Rendered on the login
      // card and cleared only when the user dismisses it or signs in.
      ssoError: null,
      // A pending UNLINKED EdCity sign-in ({pendCode, schoolKnown, staff}), or
      // null. Set from the ?edconnect=2 return leg; drives the choice dialog.
      edcityPending: null,
    };

    autoBind(this);
    // The language is resolved once in Localization.jsx (stored choice, else the
    // 'tc' default). Forcing it here ran on every mount and threw away whatever
    // the user had picked in Settings.


    // TODO: have some language BUGs, eric will resolve them
    this.state.status_titles = [
      Localization.strings.LOCALE_LIST,
      Localization.strings.HOTSPOT_EDIT,
      Localization.strings.RETRIEVER
    ];

  }

  componentWillMount(){
    this.unlistenHistory = this.props.history.listen(this.historyOnChange);

    // Add debugging to track all history changes
    // Guard against double-wrapping: history is a shared singleton, so on a
    // remount we must not stack wrappers on top of the already-wrapped functions.
    if (!this.props.history.__rootPagePatched) {
      const originalPush = this.props.history.push;
      const originalReplace = this.props.history.replace;
      this.props.history.__rootPageOriginalPush = originalPush;
      this.props.history.__rootPageOriginalReplace = originalReplace;

      this.props.history.push = (path, state) => {
        if (Config.IS_DEBUG()) {
          console.log('🔄 HISTORY PUSH:', path, 'from:', this.props.history.location.pathname);
          console.trace('Push called from:');
        }
        return originalPush.call(this.props.history, path, state);
      };

      this.props.history.replace = (path, state) => {
        if (Config.IS_DEBUG()) {
          console.log('🔄 HISTORY REPLACE:', path, 'from:', this.props.history.location.pathname);
          console.trace('Replace called from:');
        }
        return originalReplace.call(this.props.history, path, state);
      };

      this.props.history.__rootPagePatched = true;
    }
  }

  componentWillUnmount(){
    // Unsubscribe the history listener registered in componentWillMount
    if (this.unlistenHistory) {
      this.unlistenHistory();
      this.unlistenHistory = null;
    }

    // Restore the original push/replace so the debug wrappers don't leak
    // (and don't stack if RootPage is later remounted)
    if (this.props.history.__rootPagePatched) {
      if (this.props.history.__rootPageOriginalPush) {
        this.props.history.push = this.props.history.__rootPageOriginalPush;
      }
      if (this.props.history.__rootPageOriginalReplace) {
        this.props.history.replace = this.props.history.__rootPageOriginalReplace;
      }
      delete this.props.history.__rootPageOriginalPush;
      delete this.props.history.__rootPageOriginalReplace;
      delete this.props.history.__rootPagePatched;
    }
  }

  componentDidMount() {
    // When the refresh token is gone or rejected there is nothing left to
    // retry, so bounce back to the login screen instead of leaving the user on
    // a page where every request 401s.
    AuthService.setSessionExpiredHandler(() => {
      // A sign-in that is mid-flight or has just reported its OWN failure owns
      // the message. `status` alone cannot express that: React commits it
      // asynchronously, so the flag (set synchronously by
      // completeEdConnectLogin) is what closes the gap.
      if (this.suppressSessionExpired > 0) {
        console.warn('🔐 suppressed a session-expired from the outgoing session');
        return;
      }
      if (this.state.status !== STATUS_REQUIRE_LOGIN) {
        this.setState({ isLoading: false, status: STATUS_REQUIRE_LOGIN });
        this.addNotification(Localization.strings.SESSION_EXPIRED || 'Session expired, please sign in again.', false);
        this.replaceHistory("/");
      }
    });

    const script = document.getElementById('google-maps-script');
    const notifyGoogleMapsReady = () => {
      window.dispatchEvent(new Event('google-maps-ready'));
    };
    const notifyGoogleMapsError = () => {
      window.dispatchEvent(new Event('google-maps-error'));
    };

    if (window.google?.maps?.places) {
      notifyGoogleMapsReady();
    } else {
      script.onload = notifyGoogleMapsReady;
      script.onerror = notifyGoogleMapsError;
    }
    script.src = `https://maps.googleapis.com/maps/api/js?key=${Config.GMAP_KEY()}&libraries=places`;

    // The OAuth return markers are read BEFORE anything may act on the stored
    // session, because on a LOGIN return leg the stored session must not be
    // revived at all.
    const params = new URLSearchParams(window.location.search);
    const edconnect = params.get('edconnect');

    // The three LOGIN legs — 0 refused, 1 success, 2 unlinked-and-pending — all
    // end in a session that is about to be REPLACED, refused, or chosen. Reviving
    // the stored one here is what produced the spurious SESSION_EXPIRED toast
    // right after a good EdCity sign-in: a stale blob whose expiresAt is still in
    // the future but whose tokens the server no longer honours took
    // checkExistingSession's auto-login branch, and setLogin immediately fired
    // updateUserInfo / getKLAAndSubjectFromServer / getSubscriptionDetailBySchoolId
    // with the dead access token. Each 401 refreshed once with the equally dead
    // refresh token, got REFRESH_REJECTED, and that path runs clearSession() +
    // notifySessionExpired() — which both raises this toast AND can wipe the token
    // pair the concurrent code exchange has just stored.
    // The two return legs of BINDING are the opposite case and must NOT be
    // skipped: they use a different param (?edconnect_bind=), the user was already
    // signed in when they started the bind, and their session has to be restored
    // on the way back or the bind lands them on the login screen.
    // 0 and 2 skip for a second reason: LoginPage renders only under
    // STATUS_REQUIRE_LOGIN, so an auto-login there swallows the refusal card and
    // unmounts the login form the EdCity choice dialog links through.
    const isOAuthLoginReturn = (edconnect === '0' || edconnect === '1' || edconnect === '2');
    if (!isOAuthLoginReturn) {
      // Check for existing valid session on page load
      this.checkExistingSession();
    }

    // If redirected back from EdConnect OAuth, redeem the one-time code.
    if (edconnect === '1') {
      const evCode = params.get('ev_code');
      // Strip the markers BEFORE redeeming. The code is single-use, so a reload
      // with it still on the URL would spend a second attempt on a code the
      // server has already consumed and land on a spurious failure. The hash is
      // preserved — under HashRouter it is the route the user was on.
      const cleanUrl = window.location.origin + window.location.pathname
        + window.location.hash;
      window.history.replaceState({}, document.title, cleanUrl);
      if (evCode) {
        // Drop the outgoing session BEFORE redeeming. Skipping the auto-login
        // above stops the setLogin cascade, but it cannot un-fire what the first
        // render already dispatched: the constructor puts the app in
        // STATUS_VIEW_LOCALE the moment it sees ?edconnect=1, so
        // TopBar.componentDidMount's getSubscriptionDetailBySchoolId goes out
        // with the stale bearer BEFORE this method runs — children mount before
        // their parent. (LocaleViewerPage's constructor also calls
        // refreshLocaleList, but that one bails inside filterLocaleList on the
        // missing user/school id, so it is a no-op here by accident rather than
        // by design — do not rely on it staying one.) Clearing here
        // means those 401s find no refresh token to reject, so nothing can later
        // clear the pair completeEdConnectLogin is about to store. clearSession()
        // itself does not notify; the matching suppression that keeps those late
        // 401s from raising the expiry toast lives in AuthService.
        AuthService.clearSession();
        this.completeEdConnectLogin(evCode);
      } else {
        // edconnect=1 with no code cannot happen from our own callback, but a
        // hand-edited URL reaches here — and the constructor has already put the
        // app into a loading state that nothing else would clear.
        this.setState({ isLoading: false, status: STATUS_REQUIRE_LOGIN });
      }
    } else if (edconnect === '2') {
      // An UNLINKED EdCity identity. The callback did not refuse it outright —
      // it parked the identity in a single-use pending ticket and sent back the
      // ticket's opaque code plus two rendering hints. The choice dialog peeks
      // at the ticket (it is NOT consumed until the user picks), so unlike the
      // ev_code above there is nothing here that a reload could spend — the
      // params are still stripped so a reload simply lands on the login page.
      const pendCode = params.get('pend_code');
      const schoolKnown = params.get('school_known') === '1';
      const staff = params.get('staff') === '1';
      this.clearOAuthParams();
      if (pendCode) {
        this.setState({
          edcityPending: { pendCode: pendCode, schoolKnown: schoolKnown, staff: staff },
        });
      }
    } else if (edconnect === '0') {
      // A REFUSED SIGN-IN, shown on the login card and left there.
      //
      // This used to be a 6-second toast (Notification autoCloses at 6000ms),
      // which is the wrong affordance for "you cannot get in": the user is
      // mid-redirect when it fires, and if they blink they are returned to a
      // login screen that looks exactly as it did before, with no explanation.
      // A refusal has to persist until dismissed.
      this.clearOAuthParams();
      this.setState({ ssoError: params.get('edconnect_error') || 'login_failed' });
    } else if (params.get('edconnect_bind') === '1') {
      // EDConnect binding success. `status` is success|already_linked; both are
      // a good outcome from the user's side, so they read the same message.
      this.clearOAuthParams();
      this.addNotification(Localization.strings.EDCONNECT_BIND_SUCCESS, true);
    } else if (params.get('edconnect_bind') === '0') {
      // EDConnect binding failed. Note the key is `error`, not
      // `edconnect_error` — the bind leg has its own vocabulary.
      const err = params.get('error');
      this.clearOAuthParams();
      if (err === 'already_linked_to_other') {
        this.addNotification(Localization.strings.EDCONNECT_BIND_ALREADY_OTHER, false);
      } else if (err === 'school_mismatch') {
        // The account's school and the EdCity school both resolved, and to
        // DIFFERENT schools — the server refuses the link.
        this.addNotification(Localization.strings.EDCONNECT_BIND_SCHOOL_MISMATCH, false);
      } else if (err === 'already_linked_created') {
        // The conflicting link's owner is an account auto-provisioned from
        // this very EdCity identity — the no-migrate reminder, not the generic
        // "already linked" copy.
        this.addNotification(Localization.strings.EDCITY_CREATED_ACCOUNT_NO_MIGRATE, false);
      } else {
        this.addNotification(Localization.strings.EDCONNECT_BIND_FAILED, false);
      }
    }
  }

  /**
   * Drop the `?edconnect*` markers so a reload does not re-handle them.
   *
   * The HASH is deliberately kept. The bind flow starts from a signed-in user
   * who is somewhere in the app (Settings, a locale, the retriever), and under
   * HashRouter that position lives entirely in the fragment — stripping it
   * teleports them to the root right after a successful bind.
   */
  clearOAuthParams() {
    window.history.replaceState(
      {},
      document.title,
      window.location.origin + window.location.pathname + window.location.hash,
    );
  }

  /**
   * Close the unlinked-EdCity choice dialog. The pending ticket is left alone:
   * it was only ever peeked, and the server expires it on its own TTL.
   */
  closeEdCityPendingDialog() {
    this.setState({ edcityPending: null });
  }

  checkExistingSession() {
    console.log('🔐 checkExistingSession started');
    const userData = localStorage.getItem('userdata');
    if (userData) {
      try {
        const parsedData = JSON.parse(userData);
        
        // Check if session has expiration data (new format).
        // A stored blob carrying no JWT pair is a PRE-MIGRATION session: it
        // would auto-login into a UI where every /api call 401s, so treat it
        // like the legacy format below and drop it.
        if (parsedData.expiresAt && AuthService.isLoggedIn()) {
          const now = new Date().getTime();
          if (now < parsedData.expiresAt) {
            // Session is still valid, auto-login
            console.log('🔐 Auto-login: Valid session found');

            // Slide the window forward. expiresAt is only ever read HERE, so an
            // absolute cap measured from the login (2h without "remember me")
            // logs the user out mid-work: every page reload after that mark
            // lands on the login screen even though the refresh token is good
            // for 30 days — and in a dev/edit session the page reloads
            // constantly. Re-stamping on each successful auto-login makes the
            // cap what it reads as: 2h of INACTIVITY, not 2h of use. The
            // refresh token's own exp still caps it (see persistSession).
            AuthService.persistSession(parsedData.loginResponse, Boolean(parsedData.rememberMe));

            // Store current URL before auto-login
            const currentPath = this.props.history.location.pathname;
            console.log('🔐 Auto-login: Current path:', currentPath);
            
            // Call regular setLogin which now preserves URLs
            console.log('🔐 Auto-login: Calling setLogin with auto-login flag');
            this.setLogin(parsedData.loginResponse, true); // true indicates auto-login
          } else {
            // Session expired, remove it
            console.log('🔐 Auto-login: Session expired');
            localStorage.removeItem('userdata');
          }
        } else {
          // Old format without expiration, assume expired and remove
          console.log('🔐 Auto-login: Old session format found, removing');
          localStorage.removeItem('userdata');
        }
      } catch (error) {
        console.error('🔐 Auto-login: Error parsing stored session:', error);
        localStorage.removeItem('userdata');
      }
    } else {
      console.log('🔐 checkExistingSession: No stored session found');
    }
    console.log('🔐 checkExistingSession completed');
  }

  setLogin(res, isAutoLogin = false){
    // Sucessful login
    console.log('🔑 setLogin called, isAutoLogin:', isAutoLogin, 'current path:', this.props.history.location.pathname);
    
    // 1. update user information from server and store locally
    if(this.refs.userDataProfile === undefined){return}
    this.refs.userDataProfile.updateUserInfo(res);
    // 2. update school information from server and store locally
    this.refs.userDataProfile.updateSchoolInfo(res);
    // 3. update kla and subject information from server and store locally
    this.refs.userDataProfile.getKLAAndSubjectFromServer();

    // Mark fresh manual login to only show storage dialog once
    this.setState({ isFreshLogin: !isAutoLogin });
    this.state.isFreshLogin = !isAutoLogin;

    // 4. fetch subscription/privilege details immediately after login
    this.getSubscriptionDetailBySchoolId();

    this.changeStatus(STATUS_VIEW_LOCALE);
    this.fetchMaintenanceMessage();

    // 5. image→3D handshake + background job registry. The handshake decides
    // self-host vs cloud generation (Config.AI_3D_EFFECTIVE_MODE); the manager
    // then re-attaches to any generation jobs recorded before a reload.
    this.fetchAi3dSystemConfig(() => {
      ThreeDJobManager.init(this);
    });
    
    // Only redirect for non-auto-login or when not on a valid locale path
    const currentPath = this.props.history.location.pathname;
    const isValidLocalePath = ["/list", "/map", "/library"].includes(currentPath);
    
    console.log('🔑 setLogin path analysis:', {
      currentPath,
      isValidLocalePath,
      isAutoLogin,
      shouldRedirect: !isAutoLogin && !isValidLocalePath
    });
    
    if (!isAutoLogin && !isValidLocalePath) {
      console.log('🔑 setLogin: Regular login redirecting to list view');
      this.pushHistory("/list");
    } else {
      console.log('🔑 setLogin: Preserving current path or auto-login, no redirect');
      // For third-party login at root path, we should still redirect to /list
      if (!isAutoLogin && currentPath === '/') {
        console.log('🔑 setLogin: Third-party login at root, redirecting to /list');
        this.pushHistory("/list");
      }
    }
  }

  historyOnChange(location) {
    console.log('historyOnChange:', location.pathname, this.props.history.action);
    // Safety check for refs object
    if (!this.refs) {
      console.warn('Refs object not available in historyOnChange');
      return;
    }
    const { key } = location;
    let goBack = false;
    // If there is no key, it was a goBack.
    if (key === undefined) {
      goBack = true;
      //return;
    }
    // If it's an entirely new key, it was a goForward.
    // If it was neither of the above, you can compare the index
    // of `key` to the previous key in your keys array.
    if (!keys.includes(key)) {
      keys.push(key);
    } else if (keys.indexOf(key) < keys.indexOf(previousKey)) {
      goBack = true;
    }
    previousKey = key;

    let path = this.props.history.location.pathname;

    if (this.props.history.action === "POP") {
      if (this.state.status == STATUS_VIEW_RETRIEVER) {
        if (this.refs.retrieverPage) {
          if (goBack) {
            this.refs.retrieverPage.handleBackButtonClicked();
          }
          else {
            this.refs.retrieverPage.handleForwardButtonClicked();
          }
        }
      }
      else if (this.state.status == STATUS_VIEW_HOTSPOT) {
        if (this.refs.hotspotViewerPage) {
          if (goBack) {
            this.refs.hotspotViewerPage.handleBackButtonClicked();
          }
          else {
            this.refs.hotspotViewerPage.handleForwardButtonClicked();
          }
        }
      }
      else if (this.state.status == STATUS_VIEW_LOCALE) {
        if (this.refs.localeViewerPage) {
          if (path === "/locale/hotspot" && this.props.history.location.state && this.props.history.location.state.locale) {
            this.refs.localeViewerPage.editLocale(this.props.history.location.state.locale, false);
          }
          else if ((path === "/locale/retriever" || path === "/locale/retriever/date") && this.props.history.location.state && this.props.history.location.state.locale) {
            this.refs.localeViewerPage.retrieveAnswer(this.props.history.location.state.locale, false)
          }
          else if (path === "/list" || path === "/map" || path === "/library") {
            // Handle view changes for menu items
            this.refs.localeViewerPage.handleURLViewChange(path);
          }
        }
      }
    }
    else if (this.props.history.action === "PUSH") {
      // Handle PUSH actions for new URLs
      if (this.state.status == STATUS_VIEW_LOCALE && this.refs.localeViewerPage) {
        if (path === "/list" || path === "/map" || path === "/library") {
          this.refs.localeViewerPage.handleURLViewChange(path);
        }
      }
    }

    if (path != "/" && this.state.status == STATUS_REQUIRE_LOGIN) {
      this.replaceHistory("/");
    }
  }

  pushHistory(path, object=null) {
    // Prevent infinite URL loops by checking if we're already on a similar path
    const currentPath = this.props.history.location.pathname;
    
    // Check for URL loop patterns
    if (currentPath.includes('/locale/retriever') && path.includes('locale/retriever')) {
      console.log('🚫 PREVENTING URL LOOP: current=', currentPath, 'trying to push=', path);
      // Use replace instead of push to avoid accumulating paths
      this.props.history.replace(path, object);
      return;
    }
    
    // Check for repeated path segments (like /locale/retriever/locale/retriever)
    if (currentPath.includes(path.replace(/^\/+/, ''))) {
      console.log('🚫 PREVENTING DUPLICATE PATH: current=', currentPath, 'trying to push=', path);
      return;
    }
    
    console.log('🔄 pushHistory called with:', path, 'from:', currentPath);
    console.log('✅ ALLOWING pushHistory to:', path);
    this.props.history.push(path, object);
  }

  goBackHistory() {
    this.props.history.goBack();
  }

  replaceHistory(path) {
    this.props.history.replace(path);
  }

  loginOK(res, rememberMe = false){
    // AuthService owns the 'userdata' blob now: it writes loginResponse /
    // expiresAt / rememberMe / createdAt exactly as before, and MERGES the JWT
    // pair in as siblings. Rewriting the key here directly (as this used to)
    // would drop the tokens that AuthService.login had just stored.
    AuthService.persistSession(res, rememberMe);
    this.setLogin(res);
  }

  /**
   * Finish an EdCity sign-in: trade the one-time `ev_code` from the redirect
   * for a JWT pair.
   *
   * This replaces the old PHPSESSID assumption. The OAuth round trip used to
   * end by setting a cookie, which the bearer surface cannot use; it now ends
   * with a code that POST /api/auth/edconnect/exchange redeems. The code is
   * single-use and expires in two minutes, so there is exactly one attempt —
   * a retry after a network failure will legitimately fail, and the user has
   * to press the button again.
   */
  completeEdConnectLogin(code){
    console.log('🔄 EdConnect: redeeming one-time code');
    // AuthService mutes the session-expired terminal only for the duration of
    // the exchange itself, and the handler below suppresses on `status`, which
    // React commits asynchronously. Between those two lies a real gap: on a
    // REFUSED exchange (a burnt single-use code — a reload is enough) the mute
    // has already lifted and the status set two lines down has not landed yet,
    // so a straggling 401 from the requests TopBar fired at mount would raise
    // "session expired" on top of the accurate "sign-in failed". This counter
    // is synchronous, so it covers the whole window; it is released on a later
    // macrotask precisely so the release cannot beat React's commit.
    this.suppressSessionExpired = (this.suppressSessionExpired || 0) + 1;
    AuthService.exchangeEdConnectCode(code)
      .then((res)=>{
        // exchangeEdConnectCode already stored the token pair; from here this
        // is an ordinary successful login.
        this.adoptSsoSession(res);
      })
      .catch((e)=>{
        console.warn('🔄 EdConnect: code exchange refused', e && e.status);
        this.setState({ isLoading: false, status: STATUS_REQUIRE_LOGIN });
        this.addNotification(Localization.strings.THIRD_APRTY_LOGIN_FAILED, false);
      })
      .finally(()=>{
        setTimeout(()=>{ this.suppressSessionExpired -= 1; }, 0);
      });
  }

  /**
   * Adopt a session that arrived from a REDIRECT rather than from the login
   * form, and land the user on the locale list.
   *
   * Kept separate from loginOK because the entry conditions differ: there is no
   * "remember me" switch on a redirect, and the app is currently painting a
   * loading state (isLoading was set in the constructor the moment the
   * `?edconnect=1` marker was seen) that has to be cleared here.
   */
  adoptSsoSession(res){
    // Persist with expiresAt, as a normal login does. checkExistingSession()
    // treats a stored session with no expiresAt as the legacy "old format" and
    // deletes it on the next page load, which would log the user straight back
    // out on their first reload.
    AuthService.persistSession(res, false);

    // isAutoLogin = true suppresses setLogin's own redirect; the push below is
    // the deliberate one.
    this.setLogin(res, true);

    setTimeout(() => {
      this.setState({ isLoading: false });
      if (this.refs && this.refs.localeViewerPage) {
        this.refs.localeViewerPage.refreshLocaleList();
      }
      this.pushHistory("/list");
    }, 100);
  }

  pullSessionFromServer(){
    console.log('🔄 pullSessionFromServer: Starting session retrieval');
    // GET /api/auth/session — replaces session_json.php. It is a BEARER route:
    // it re-reads the user/school for a token this client ALREADY holds, so it
    // restores a session and cannot create one. The EdConnect return leg does
    // not come through here — it arrives with no token at all and mints one via
    // completeEdConnectLogin above.
    AuthService.fetchSession()
      .then((data)=>{
        console.log('🔄 pullSessionFromServer: session restored:', Boolean(data));
        if (data && data.login_result === 1) {
          this.adoptSsoSession(AuthService.makeResponse(200, data));
        } else {
          console.log('🔄 pullSessionFromServer: Login failed, data:', data);
          // Turn off loading and show login page
          this.setState({ isLoading: false, status: STATUS_REQUIRE_LOGIN });
          this.addNotification(Localization.strings.THIRD_APRTY_LOGIN_FAILED, false);
        }
      })
      .catch((e)=>{
        console.error('🔄 pullSessionFromServer error', e);
        // Turn off loading and show login page
        this.setState({ isLoading: false, status: STATUS_REQUIRE_LOGIN });
        this.addNotification('Unable to retrieve login session', false);
      });
  }

  changeStatus(newStatus) {
    console.log('🔄 changeStatus called:', 'from', this.state.status, 'to', newStatus);
    this.setState({
      status: newStatus
    });
    this.state.status = newStatus;
    console.log('🔄 changeStatus completed, new status:', this.state.status);

    if (this.refs.localeViewerPage != null) {
      this.refs.localeViewerPage.setState({ active: (newStatus == STATUS_VIEW_LOCALE) });
    }
  }

  setCurrentLocale(locale) {
    this.setState({ currentLocale: locale });
    this.state.currentLocale = locale;
  }

  getCurrentLocale() {
    return this.state.currentLocale;
  }

  languageChanged() {
    this.forceUpdate();
  }

  showLoading() {
    this.setState({ isLoading: true });
    this.state.isLoading = true;
  }

  hideLoading() {
    this.setState({ isLoading: false });
    this.state.isLoading = false;
  }

  addNotification(message, success=true) {
    this.refs.notification.add(message, success);
  }

  // Startup handshake for image→3D generation. An updated server answers
  // ai3dGenMode ("self"/"cloud") and optionally ai3dSelfApiUrl; an older server
  // omits the keys and Config falls back to the safe cloud pipeline. `done`
  // runs either way — the caller starts the job manager with whatever mode
  // resolution ended up in effect.
  fetchAi3dSystemConfig(done) {
    const finish = () => { if (typeof done === "function") done(); };
    if (!this.refs.API) {
      finish();
      return;
    }
    this.refs.API.request(
      "get_system_config",
      {},
      (res) => {
        const cfg = res && res.body && res.body.result ? res.body.result : {};
        Config.SET_AI_3D_HANDSHAKE({ mode: cfg.ai3dGenMode, selfApiUrl: cfg.ai3dSelfApiUrl });
        finish();
      },
      (err) => {
        console.warn("get_system_config handshake failed; keeping compile-time 3D mode", err);
        finish();
      }
    );
  }

  fetchMaintenanceMessage() {
    if (!this.refs.API) {
      return;
    }

    const userId = this.getMyUserId();
    const requestObj = { 
      kind: "composer",
      user_id: userId
    };

    this.refs.API.request(
      "get_maintenance_message",
      requestObj,
      (res) => {
        const result = res && res.body ? res.body.result : null;
        let message = null;
        if (Array.isArray(result)) {
          message = result.length > 0 ? result[0] : null;
        } else if (result && typeof result === 'object') {
          message = result;
        }
        if (!message) return;

        const messageId = message.id;
        const lang = Localization.strings.getLanguage();
        let title = "";
        let body = "";
        if (lang === "en") {
          title = message.title_en || "";
          body = message.body_en || "";
        } else if (lang === "tc" || lang === "zh") {
          title = message.title_zh || "";
          body = message.body_zh || "";
        } else {
          title = message.title || "";
          body = message.body || "";
        }

        if (!title && !body) {
          // Fallback to any available fields to avoid silent failures
          title = message.title || message.title_en || message.title_zh || "";
          body = message.body || message.body_en || message.body_zh || "";
        }

        if (!title && !body) {
          // As a last resort, show raw payload for visibility
          title = Localization.strings.TITLE;
          try {
            body = JSON.stringify(message, null, 2);
          } catch (_) {
            body = String(message);
          }
        }

        const showDialog = () => {
          if (!this.refs.confirmDialog) {
            return false;
          }
          this.refs.confirmDialog.show(
            {
              title: title || Localization.strings.TITLE,
              content: body || Localization.strings.CONTENT,
              confirm: Localization.strings.OK,
              cancel: Localization.strings.DO_NOT_SHOW_AGAIN,
            },
            () => {
              // OK button - call API with do_not_show = 0
              this.markMaintenanceMessageRead(userId, messageId, 0);
            },
            () => {
              // Don't Show Again button - call API with do_not_show = 1
              this.markMaintenanceMessageRead(userId, messageId, 1);
            },
            () => {
              // Dialog closed without clicking any button (clicked outside) - call API with do_not_show = 0
              this.markMaintenanceMessageRead(userId, messageId, 0);
            }
          );
          return true;
        };

        if (!showDialog()) {
          setTimeout(() => {
            showDialog();
          }, 200);
        }
      },
      () => {}
    );
  }

  markMaintenanceMessageRead(userId, messageId, doNotShow) {
    if (!this.refs.API || !userId || !messageId) {
      return;
    }

    this.refs.API.request(
      "maintenance_message_read",
      {
        user_id: userId,
        message_id: messageId,
        do_not_show: doNotShow
      },
      (res) => {
        // Success - no need to show notification
        console.log('Maintenance message read status updated:', res);
      },
      (err) => {
        // Error - log but don't bother the user
        console.error('Failed to update maintenance message read status:', err);
      }
    );
  }

  render() {
    console.log('🎨 render() called with status:', this.state.status, 'STATUS_REQUIRE_LOGIN:', STATUS_REQUIRE_LOGIN);

    return (
      // <ThemeProvider theme={theme}> // REMOVED
      <div>
        {/* <link rel="stylesheet" href="https://fonts.googleapis.com/css?family=Noto+Sans+TC:300,400,500,700&display=swap" /> */}
        <div>
          <API root={this} parent={this} ref="API"/>
          <UserDataProfile root={this} parent={this} ref="userDataProfile"/>
          <Loading root={this} parent={this} isLoading={this.state.isLoading}/>
          <ConfirmDialog root={this} parent={this} ref="confirmDialog"/>
          <Notification root={this} parent={this} ref="notification"/>
          <EdCityUnlinkedDialog root={this} parent={this}
            pending={this.state.edcityPending}
            onClose={this.closeEdCityPendingDialog}
          />

          <div style={{
            ...(this.state.isLoading ? styles.loadingOverlay : styles.none),
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh'
          }}>
            <If condition={this.state.status != STATUS_REQUIRE_LOGIN}>
              <Then>
                <TopBar root={this} parent={this} ref="topBar"/>
                <LocaleViewerPage root={this} parent={this} ref="localeViewerPage"/>
              </Then>
            </If>


            <If condition={Config.IS_UNIT_TEST() === true}>
              <Then>
                <LoginCreateSession parent={this} root={this} ref="loginCreateSession"
                  username="" password=""
                  loginOKFunction = {this.loginOK}
                  loginFailFunction = {null}/>
              </Then>
            </If>

            <Switch condition={this.state.status}>

              <Case value={STATUS_REQUIRE_LOGIN}>
                {/* ref: the EdCity choice dialog finishes its log-in-&-link leg
                    through LoginPage.loginOK so the T&C gate still runs. */}
                <LoginPage
                  root={this}
                  parent={this}
                  ref="loginPage"
                  ssoError={this.state.ssoError}
                  onDismissSsoError={() => this.setState({ ssoError: null })}
                />
              </Case>

              <Case value={STATUS_VIEW_HOTSPOT}>
                <HotspotViewerPage root={this} parent={this} currentLocale={this.state.currentLocale} ref="hotspotViewerPage"/>
              </Case>

              <Case value={STATUS_VIEW_RETRIEVER}>
                <RetrieverPage root={this} parent={this} ref="retrieverPage"
                  localeData={this.state.currentLocale} 
                />
              </Case>

            </Switch>
          </div>
        </div>
      </div>
      // </ThemeProvider> // REMOVED
    )
  }

  // data functions
  getMyUserId(){
    return this.refs.userDataProfile ? this.refs.userDataProfile.state.userInfo.id : null;
  }
  getMySchoolId(){ 
    return this.refs.userDataProfile ? this.refs.userDataProfile.state.userInfo.school_id : null;
  }

  getSameSchoolEdit() {
    return this.refs.userDataProfile ? parseInt(this.refs.userDataProfile.state.userInfo.role) >= 1 : false;
  }

  getSubscriptionDetailBySchoolId() {
    // Safety check for refs
    if (!this.refs || !this.refs.API) {
      console.warn('API ref not available yet, skipping subscription detail request');
      return;
    }
    
    let requestObj = {
      school_id: this.getMySchoolId()
    }
    // get privilege detail by school id
    this.refs.API.request("get_privilege_detail_by_school_id", requestObj, this.getSubscriptionDetailBySchoolIdOK, this.getSubscriptionDetailBySchoolIdFail);
    // get_subscription_list_by_school_id
    this.refs.API.request("get_subscription_list_by_school_id", requestObj, this.getSubscriptionListBySchoolIdOK, this.getSubscriptionListBySchoolIdFail);
  }

  getSubscriptionListBySchoolIdOK(res) {
    // console.log("getSubscriptionListBySchoolIdOK", res.body.result);
    // Handle the successful response here.
    // On the OK path res.body.result may be null/undefined/non-array (see API.jsx contract),
    // and updateSubscriptionPlanNames loops on .length, so normalise to an array first.
    const list = res && res.body && Array.isArray(res.body.result) ? res.body.result : [];
    this.refs.userDataProfile.updateSubscriptionPlanNames(list);
  }

  getSubscriptionListBySchoolIdFail(err) {
    console.log("getSubscriptionListBySchoolIdFail", err);
  }

  getSubscriptionDetailBySchoolIdOK(res) {
    console.log("getSubscriptionDetailBySchoolIdOK", res && res.body ? res.body.result : null);
    // On the OK path res.body.result may be null/undefined/non-object (e.g. a school with no
    // privilege row). Bail out instead of throwing on .detail so post-login setup still runs.
    const result = (res && res.body && res.body.result && typeof res.body.result === 'object') ? res.body.result : null;
    if (!result) {
      return;
    }
    this.refs.userDataProfile.updateSubscriptionInfo(result.detail);
    this.refs.userDataProfile.updateSubscriptionPlan(result.subscription);
    this.refs.userDataProfile.updateSchoolStorageUsed(result.storage_used);
    this.refs.userDataProfile.updateEpackCreated(result.locale_count);

    // Check storage limit and prompt if exceeded
    try {
      const detail = res.body.result.detail || [];
      const storageUsedKB = Number(res.body.result.storage_used) || 0;
      const storagePrivilege = detail.find(p => p.privilege_name === 'STORAGE_LIMIT');
      const storageLimitMB = storagePrivilege ? Number(storagePrivilege.value) : 0;
      const storageLimitKB = storageLimitMB * 1024;
      if (
        (typeof Config.SUBSCRIPTION_FLAG === 'function' && Config.SUBSCRIPTION_FLAG() === true) &&
        storagePrivilege && storageLimitKB > 0 && storageUsedKB >= storageLimitKB && this.state.isFreshLogin === true
      ) {
        if (this.state.hasShownStorageLimitDialog !== true) {
          const link = (typeof Config.SUBSCRIPTION_MANAGE_URL === 'function')
            ? Config.SUBSCRIPTION_MANAGE_URL()
            : (window.location.origin + '/');
          this.refs.confirmDialog.show(
            {
              title: Localization.strings.STORAGE_LIMIT_REACHED,
              content: Localization.strings.UPGRADE_TO_PREMIUM,
              confirm: (typeof Config.SUBSCRIPTION_LINK_FLAG === 'function' && Config.SUBSCRIPTION_LINK_FLAG() === true) ? Localization.strings.OPEN_SUBSCRIPTION_PAGE : Localization.strings.CONFIRM,
              cancel: Localization.strings.CLOSE,
            },
            () => {
              if (typeof Config.SUBSCRIPTION_LINK_FLAG === 'function' && Config.SUBSCRIPTION_LINK_FLAG() === true) {
                window.open(link, '_blank', 'noopener,noreferrer');
              }
            },
            () => {}
          );
          this.setState({ hasShownStorageLimitDialog: true });
          this.state.hasShownStorageLimitDialog = true;
          // prevent re-show on refresh of /list within same login session
          this.setState({ isFreshLogin: false });
          this.state.isFreshLogin = false;
        }
      }
    } catch (e) {
      console.warn('Storage limit check failed:', e);
    }
  }

  getSubscriptionDetailBySchoolIdFail(err) {
    console.log("getSubscriptionDetailBySchoolIdFail", err);
  }

  getPrivilege(privilegeName) {
    if (this.refs.userDataProfile) {
      return this.refs.userDataProfile.getPrivilege(privilegeName);
    }
    // Return a default safe object if userDataProfile is not available
    return {
      privilege_name: "",
      privilege_name_long_zh: "",
      privilege_name_long_en: "",
      value_type: "",
      description: "",
      value: "",
      value_2: ""
    };
  }

  isAdminUser() {
    return (this.refs.userDataProfile && this.getMySchoolId() == 3 && this.refs.userDataProfile.state.userInfo.role == 2);
  }

  setTitle(newTitle){
    if(this.refs.topBar != undefined){
      this.refs.topBar.setState({title: newTitle});
    }
  }
}

const withRouterAndRef = Wrapped => {
  const WithRouter = withRouter(({ forwardRef, ...otherProps }) => (
    <Wrapped ref={forwardRef} {...otherProps} />
  ))
  const WithRouterAndRef = React.forwardRef((props, ref) => (
    <WithRouter {...props} forwardRef={ref} />
  ))
  const name = Wrapped.displayName || Wrapped.name
  WithRouterAndRef.displayName = `withRouterAndRef(${name})`
  return WithRouterAndRef
}

export default withRouterAndRef(RootPage);
